import { Component } from "@theme/component";

const THUMBS_BREAKPOINT = 750;

/**
 * Product media gallery using Swiper with synced thumbnails.
 * Provides slideshow API compatibility for media-gallery and zoom-dialog.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} mainSwiper - Main gallery swiper element
 * @property {HTMLElement} thumbsSwiper - Thumbnails swiper element
 * @property {HTMLElement[]} thumbnails - Thumbnail button elements
 *
 * @extends {Component<Refs>}
 */
class ProductMediaSwiper extends Component {
  static get observedAttributes() {
    return ["data-media-count"];
  }

  /** @type {import('swiper').Swiper | null} */
  #mainSwiper = null;

  /** @type {import('swiper').Swiper | null} */
  #thumbsSwiper = null;

  /** @type {HTMLElement[]|null} Original, unfiltered slide order */
  #allSlides = null;

  /** @type {HTMLElement[]} Original, unfiltered thumbnail order */
  #allThumbs = [];

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  /** @type {((ev: MediaQueryListEvent) => void) | null} */
  #mediaQueryHandler = null;

  async connectedCallback() {
    super.connectedCallback();

    const mediaCount = parseInt(this.dataset.mediaCount || "1", 10);
    if (mediaCount <= 1) return;

    await this.#initSwiper();
    this.#setupResizeListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaQuery?.removeEventListener("change", this.#mediaQueryHandler);
    this.#mediaQuery = null;
    this.#mediaQueryHandler = null;
    this.#mainSwiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#mainSwiper = null;
    this.#thumbsSwiper = null;
  }

  #setupResizeListener() {
    this.#mediaQuery = window.matchMedia(`(min-width: ${THUMBS_BREAKPOINT}px)`);
    this.#mediaQueryHandler = () => {
      this.#reinitThumbs();
    };
    this.#mediaQuery.addEventListener("change", this.#mediaQueryHandler);
  }

  #getThumbsConfig() {
    const isVertical = window.matchMedia(
      `(min-width: ${THUMBS_BREAKPOINT}px)`,
    ).matches;
    return {
      spaceBetween: 8,
      slidesPerView: "auto",
      freeMode: true,
      watchSlidesProgress: true,
      direction: isVertical ? "vertical" : "horizontal",
    };
  }

  async #initSwiper() {
    const mainEl = this.refs.mainSwiper;
    const thumbsEl = this.refs.thumbsSwiper;
    if (!mainEl || !thumbsEl) return;

    let Swiper = window.Swiper;
    if (!Swiper) {
      await new Promise((resolve) => {
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js";
        script.async = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });
      Swiper = window.Swiper;
    }
    if (!Swiper) return;

    const thumbsConfig = this.#getThumbsConfig();

    // Thumbs first (required for thumbs option)
    this.#thumbsSwiper = new Swiper(thumbsEl, thumbsConfig);

    this.#mainSwiper = new Swiper(mainEl, {
      spaceBetween: 0,
      effect: "fade",
      fadeEffect: { crossFade: true },
      grabCursor: true,
      thumbs: {
        swiper: this.#thumbsSwiper,
      },
      on: {
        slideChange: () => {
          this.#resetMobileZoom();
          this.#updateThumbnailButtons();
          this.#playVideoInActiveSlide();
        },
      },
    });

    this.#updateThumbnailButtons();
    this.#playVideoInActiveSlide();
  }

  async #reinitThumbs() {
    const activeIndex = this.#mainSwiper?.activeIndex ?? 0;

    this.#mainSwiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#mainSwiper = null;
    this.#thumbsSwiper = null;

    await this.#initSwiper();
    this.#mainSwiper?.slideTo(activeIndex, 0);
    this.#updateThumbnailButtons();
  }

  /**
   * Lightweight refresh after morph updates the DOM.
   * Uses swiper.update() to recalculate positions without destroy/reinit.
   * Falls back to full reinit if slide count changed significantly.
   */
  async updateAfterMorph() {
    const mediaCount = parseInt(this.dataset.mediaCount || "1", 10);

    if (this.#mainSwiper) {
      const currentSlideCount = this.#mainSwiper.slides?.length ?? 0;
      if (mediaCount <= 1 || Math.abs(currentSlideCount - mediaCount) > 0) {
        // Slide count changed — need full reinit
        this.#mainSwiper.destroy(true, true);
        this.#thumbsSwiper?.destroy(true, true);
        this.#mainSwiper = null;
        this.#thumbsSwiper = null;
        if (mediaCount > 1) {
          await this.#initSwiper();
          this.#updateThumbnailButtons();
        }
      } else {
        // Same slide count — lightweight update
        this.#mainSwiper.update();
        this.#thumbsSwiper?.update();
        this.#updateThumbnailButtons();
      }
    } else if (mediaCount > 1) {
      // No swiper yet, initialize
      await this.#initSwiper();
      this.#setupResizeListener();
      this.#updateThumbnailButtons();
    }
  }

  #updateThumbnailButtons() {
    const thumbnails = this.refs.thumbnails;
    const activeIndex = this.#mainSwiper?.activeIndex ?? 0;
    if (!Array.isArray(thumbnails)) return;

    thumbnails.forEach((btn, i) => {
      if (btn instanceof HTMLElement) {
        // aria-current, not aria-selected: these thumbnails are plain buttons
        // with no tab/option role, and aria-selected is only allowed on those.
        // axe flagged all 7 of them as aria-allowed-attr violations (critical).
        btn.setAttribute("aria-current", String(i === activeIndex));
      }
    });
  }

  #resetMobileZoom() {
    this.querySelectorAll("mobile-pinch-zoom").forEach((zoom) => {
      if (typeof zoom.resetZoom === "function") zoom.resetZoom();
    });
  }

  /**
   * When the active slide is a video (deferred-media), load and play it.
   * Pauses video in other slides so only the selected one plays.
   */
  #playVideoInActiveSlide() {
    const slides = this.slides;
    const activeIndex = this.#mainSwiper?.activeIndex ?? 0;
    const activeSlide = slides[activeIndex];
    if (!activeSlide) return;

    slides.forEach((slide, i) => {
      const deferred = slide.querySelector("deferred-media, product-model");
      if (deferred?.pauseMedia) deferred.pauseMedia();
    });

    const deferredMedia = activeSlide.querySelector("deferred-media");
    if (!deferredMedia) return;

    deferredMedia.showDeferredMedia();
    deferredMedia.playMedia?.();
  }

  /**
   * Slideshow API: select slide by index (for zoom-dialog sync)
   * @param {number} index - Slide index
   * @param {Event} [_event]
   * @param {{ animate?: boolean }} [options]
   */
  select(index, _event, options = {}) {
    if (!this.#mainSwiper || index < 0) return;
    const animate = options.animate !== false;
    this.#mainSwiper.slideTo(index, animate ? 300 : 0);
    this.#updateThumbnailButtons();
  }

  /**
   * Rewrite each surviving thumbnail's slide index to its NEW position.
   *
   * Liquid bakes the original index into `on:click="/select/N"`, and
   * component.js reads that attribute when the click is dispatched. Left alone
   * after a filter, the two 20ct thumbnails still say /select/3 and /select/4
   * while only two slides exist, so Swiper clamps both to the last one and the
   * gallery looks stuck on the final photo.
   *
   * @param {HTMLElement[]} thumbSlides - kept thumbnail slides, in new order
   */
  #renumberThumbnails(thumbSlides) {
    thumbSlides.forEach((slide, i) => {
      const btn = slide.querySelector(".product-media-swiper__thumbnail");
      if (!btn) return;
      btn.setAttribute("on:click", `/select/${i}`);

      // Keep the screen-reader position honest too: "4 of 5" is wrong once
      // three of the five are gone. Only rewrite labels of the expected
      // "<index> ... <length>" shape, so a locale that words it differently is
      // left exactly as the translation wrote it.
      const label = btn.getAttribute("aria-label");
      const numbers = label ? label.match(/\d+/g) : null;
      if (label && numbers && numbers.length === 2) {
        let seen = 0;
        const relabelled = label.replace(/\d+/g, () =>
          String(seen++ === 0 ? i + 1 : thumbSlides.length),
        );
        btn.setAttribute("aria-label", relabelled);
      }
    });
  }

  /**
   * Show only the slides whose media id is in `ids`; pass null to show all.
   *
   * Used by the Build-Your-Ring builder to show only the photography for the
   * chosen stone size. Nothing calls it unless that product opts in.
   *
   * Slides are DETACHED from the DOM rather than hidden with CSS. Swiper keeps
   * its own index of every .swiper-slide it finds and does not understand
   * display:none -- with hidden slides left in place, update() recalculates
   * around them but the thumbs module still maps thumbnail N to the Nth slide
   * including the hidden ones, so clicking a visible thumbnail navigated to a
   * hidden slide and appeared to do nothing.
   *
   * Detaching and reinitialising means Swiper's internal state matches exactly
   * what is on screen, so thumbnails stay clickable and indices line up.
   *
   * @param {Array<string|number>|null} ids - media ids to keep, or null for all
   */
  async filterByMediaIds(ids) {
    const mainWrap = this.refs.mainSwiper?.querySelector(".swiper-wrapper");
    const thumbWrap = this.refs.thumbsSwiper?.querySelector(".swiper-wrapper");
    if (!mainWrap) return;

    // Remember the full, original set once, so filtering is always applied to
    // everything rather than to whatever survived the previous filter.
    if (!this.#allSlides) {
      this.#allSlides = Array.from(mainWrap.children);
      this.#allThumbs = thumbWrap ? Array.from(thumbWrap.children) : [];
    }

    const keep = ids && ids.length ? ids.map(String) : null;

    // Slides come back in the order the CALLER asked for, not in media order.
    // That separation matters: the product's media order decides which image
    // the collection page shows as the front image, while the gallery wants a
    // different sequence (on-body shots ahead of the variant renders). Tying
    // the two together makes one of them impossible to satisfy.
    const slideId = (slide) => {
      const holder = slide.querySelector("[data-media-id]") ||
        (slide.hasAttribute("data-media-id") ? slide : null);
      return holder ? String(holder.getAttribute("data-media-id")) : null;
    };

    let wanted;
    if (!keep) {
      wanted = this.#allSlides.slice();
    } else {
      const byId = new Map();
      const untagged = [];
      for (const slide of this.#allSlides) {
        const id = slideId(slide);
        // Slides with no media id (placeholders) always stay.
        if (!id) {
          untagged.push(slide);
          continue;
        }
        if (!byId.has(id)) byId.set(id, []);
        byId.get(id).push(slide);
      }
      wanted = [];
      for (const id of keep) {
        const slides = byId.get(id);
        if (slides) wanted.push(...slides);
      }
      wanted.push(...untagged);
    }

    // Never filter down to nothing: an empty gallery is worse than an
    // unfiltered one.
    const finalSlides = wanted.length ? wanted : this.#allSlides;

    // No change? Leave Swiper alone rather than paying for a reinit.
    const current = Array.from(mainWrap.children);
    if (current.length === finalSlides.length &&
        current.every((el, i) => el === finalSlides[i])) {
      return;
    }

    mainWrap.replaceChildren(...finalSlides);
    if (thumbWrap && this.#allThumbs.length === this.#allSlides.length) {
      // Thumbnails are matched by POSITION against the original slide order:
      // thumbnail slides carry no data-media-id of their own. Mapping through
      // that position and emitting in finalSlides order keeps each thumbnail
      // paired with its slide AND in the same sequence. Filtering the original
      // array instead would leave thumbnails in media order while the slides
      // are in caller order, so every thumbnail would open a different photo
      // than the one it shows.
      const positionOf = new Map(this.#allSlides.map((slide, i) => [slide, i]));
      const keepThumbs = finalSlides
        .map((slide) => this.#allThumbs[positionOf.get(slide)])
        .filter(Boolean);
      thumbWrap.replaceChildren(...keepThumbs);
      this.#renumberThumbnails(keepThumbs);
    }

    // The slide now at position one may never have been downloaded: it was
    // offscreen, so it is lazy and the browser has not fetched it. Promote it
    // to eager so the fetch starts immediately instead of waiting on the
    // intersection observer, which otherwise leaves the customer looking at a
    // blurred placeholder straight after choosing a metal.
    const lead = finalSlides[0];
    if (lead) {
      lead.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = "eager";
      });
    }

    // Filtering can also promote a slide whose image is ALREADY decoded (from
    // cache, or loaded earlier in the session). That image fired its one load
    // event long ago, so nothing is left to take its blurred placeholder away
    // and the ring stays blurred for good. Ask the progressive-image helper to
    // re-check the slides we just moved.
    window.progressiveImageRefresh?.(mainWrap);

    // Only reinitialise if Swiper is actually up. #initSwiper awaits a CDN
    // script, so on first load this can run while that is still in flight --
    // reinitialising there would race it and leave two Swiper instances bound
    // to the same element. The pending init reads the DOM after it resolves,
    // so it picks up the slides we just filtered on its own.
    if (!this.#mainSwiper) return;

    await this.#reinitThumbs();
    this.#mainSwiper?.slideTo(0, 0);
    this.#updateThumbnailButtons();
  }

  /**
   * Slideshow API: slides array for zoom-dialog view transition
   */
  get slides() {
    if (!this.#mainSwiper) return [];
    const slides = this.querySelectorAll(
      ".product-media-swiper__main .swiper-slide",
    );
    return Array.from(slides);
  }
}

if (!customElements.get("product-media-swiper")) {
  customElements.define("product-media-swiper", ProductMediaSwiper);
}
